import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Spinner } from '@/components/atoms/Spinner';
import SomethingHappened from '@/components/SomethingHappened';
import { useAuth, usePermissions } from '@/contexts/auth';
import { useDemoMode } from '@/contexts/demo/DemoContext';
import { useGetClientOverview } from '@/domain/client';
import { useGetLocationLicenses } from '@/domain/licenses';
import { useMarkCageHistorical } from '@/domain/locations';
import { applyDemoMaskToOverviewLocation, getDemoDisplayName } from '@/utils/demoMasking';

import RegionOverviewView from './RegionOverviewView';

interface RegionOverviewContainerProps {
    clientId: number;
}

export default function RegionOverviewContainer(props: RegionOverviewContainerProps) {
    const { clientId } = props;
    const { clients } = useAuth();
    const { isOptoScaleAdmin } = usePermissions();
    const { isDemoMode } = useDemoMode();
    const { t } = useTranslation();

    const client = clients.find((client) => client.id === clientId);

    const {
        isLoading: isLoadingOverview,
        data: clientOverview,
        isFetching: isFetchingOverview,
        error: errorOverview,
        refetch,
    } = useGetClientOverview(clientId);

    // Process locations for display (mask when demo mode is enabled)
    const displayLocations = useMemo(() => {
        if (!clientOverview?.locations || !isDemoMode) {
            return clientOverview?.locations ?? [];
        }
        return clientOverview.locations.map((loc) => applyDemoMaskToOverviewLocation(loc, t));
    }, [clientOverview?.locations, isDemoMode, t]);

    // Process client name for display (mask when demo mode is enabled)
    const displayClientName = useMemo(() => {
        if (!client?.name || !isDemoMode) {
            return client?.name;
        }
        return getDemoDisplayName(client.id, 'client', t);
    }, [client?.name, client?.id, isDemoMode, t]);

    const {
        isLoading: isloadingLocationLicense,
        isFetching: isFetchingLocationLicense,
        data: locationLicenses,
        error: errorLocationLicense,
        refetch: refetchLocationLicense,
    } = useGetLocationLicenses(clientId);

    const { mutateAsync: markHistorical } = useMarkCageHistorical({
        onSettled: () => refetch(),
    });

    const markHistoricalWrapper = async (cageId: number, historical: boolean) => {
        await markHistorical({ cageId, historical });
        //We want to block the request until we have refetched the location data
        await refetch();
    };

    const error = errorOverview || errorLocationLicense;
    const isLoading = isLoadingOverview || isloadingLocationLicense;

    // A failed query has no data either, so the error is checked before the wait for data.
    if (error) {
        return (
            <SomethingHappened
                headingLevel="h1"
                onRetry={() => {
                    if (errorOverview) void refetch();
                    if (errorLocationLicense) void refetchLocationLicense();
                }}
                isRetrying={isFetchingOverview || isFetchingLocationLicense}
            />
        );
    }

    if (isLoading || !clientOverview?.locations || !locationLicenses) {
        return <Spinner size="lg" fullScreen />;
    }

    return (
        <RegionOverviewView
            locations={displayLocations}
            locationLicenses={locationLicenses}
            clientId={clientId}
            markHistorical={markHistoricalWrapper}
            hasAccessToMarkHistorical={isOptoScaleAdmin}
            isOptoScaleAdmin={isOptoScaleAdmin}
            containerIsLoading={isLoading}
            containerError={error}
            clientName={displayClientName}
        />
    );
}
